import { Component } from '@theme/component';

class HeroResponsiveVideo extends Component {
  requiredRefs = ['fallbackImage', 'videoContainer'];

  constructor() {
    super();
    this.resizeTimeout = null;
    this.intersectionObserver = null;
    this.videoLoaded = false;
  }

  connectedCallback() {
    super.connectedCallback();

    if (!this.refs.fallbackImage) {
      this.refs.fallbackImage = this.querySelector('[ref="fallbackImage"]') || undefined;
    }
    if (!this.refs.videoContainer) {
      this.refs.videoContainer = this.querySelector('[ref="videoContainer"]') || undefined;
    }

    if (!this.refs.fallbackImage || !this.refs.videoContainer) {
      console.error('HeroResponsiveVideo: Required refs not found', {
        fallbackImage: !!this.refs.fallbackImage,
        videoContainer: !!this.refs.videoContainer,
        element: this
      });
      return;
    }

    const lazyLoad = this.dataset.lazyLoad === 'true';

    if (lazyLoad) {
      this.setupIntersectionObserver();
    } else {
      requestAnimationFrame(() => {
        this.loadVideo();
      });
    }

    window.addEventListener('resize', this.handleResize);
  }

  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) {
      this.loadVideo();
      return;
    }

    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.01
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.videoLoaded) {
          this.videoLoaded = true;
          this.loadVideo();
          if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
            this.intersectionObserver = null;
          }
        }
      });
    }, options);

    this.intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
  }

  handleResize = () => {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      const container = this.refs.videoContainer;
      const currentVideo = container instanceof HTMLElement ? container.querySelector('video') : null;
      if (!currentVideo) return;

      const isDesktop = window.innerWidth >= 768;
      const currentIsDesktop = currentVideo.dataset.deviceType === 'desktop';

      if (isDesktop !== currentIsDesktop) {
        this.loadVideo();
      }
    }, 150);
  };

  loadVideo() {
    if (!this.refs.videoContainer) {
      console.error('HeroResponsiveVideo: videoContainer ref not available');
      return;
    }

    const isDesktop = window.innerWidth >= 768;
    const videoDesktop = this.dataset.videoDesktop || '';
    const videoMobile = this.dataset.videoMobile || '';

    if (!videoDesktop.trim() && !videoMobile.trim()) {
      console.warn('HeroResponsiveVideo: No video URLs provided', {
        desktop: videoDesktop,
        mobile: videoMobile
      });
      return;
    }

    const videoUrl = isDesktop ? videoDesktop : videoMobile;
    if (!videoUrl || !videoUrl.trim()) {
      console.warn('HeroResponsiveVideo: No video URL for current breakpoint', {
        isDesktop,
        desktop: videoDesktop,
        mobile: videoMobile
      });
      return;
    }

    const container = this.refs.videoContainer;
    if (!container) return;

    const existingVideo = container instanceof HTMLElement ? container.querySelector('video') : null;
    if (existingVideo && existingVideo.dataset.deviceType === (isDesktop ? 'desktop' : 'mobile')) {
      return;
    }

    if (existingVideo) {
      existingVideo.pause();
      existingVideo.remove();
    }

    const video = document.createElement('video');
    video.className = 'hero__video hero__video--responsive';
    video.setAttribute('playsinline', '');
    video.setAttribute('autoplay', '');
    video.setAttribute('loop', '');
    video.setAttribute('muted', '');
    video.setAttribute('preload', 'auto');
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.dataset.deviceType = isDesktop ? 'desktop' : 'mobile';

    const source = document.createElement('source');
    source.src = videoUrl;
    source.type = 'video/mp4';

    video.appendChild(source);
    if (container instanceof HTMLElement) {
      container.appendChild(video);
    }

    let videoReady = false;
    let playAttempted = false;

    const attemptPlayVideo = () => {
      if (playAttempted && !video.paused) return;

      playAttempted = true;

      video.muted = true;
      video.loop = true;

      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            video.loop = true;
            video.muted = true;
          })
          .catch((error) => {
            console.warn('HeroResponsiveVideo: Autoplay blocked, video will play on interaction', error);
            playAttempted = false;

            const playOnInteraction = () => {
              video.muted = true;
              video.loop = true;
              video.play().catch(() => {});
              document.removeEventListener('click', playOnInteraction);
              document.removeEventListener('touchstart', playOnInteraction);
            };

            document.addEventListener('click', playOnInteraction, { once: true });
            document.addEventListener('touchstart', playOnInteraction, { once: true });
          });
      }
    };

    const handleVideoReady = () => {
      if (videoReady) return;
      videoReady = true;

      const fallbackEl = this.refs.fallbackImage;
      if (fallbackEl instanceof HTMLElement) {
        fallbackEl.classList.add('hero__fallback-image--hidden');
      }
      video.classList.add('hero__video--loaded');

      attemptPlayVideo();
    };

    video.addEventListener('loadeddata', handleVideoReady, { once: true });

    video.addEventListener('canplay', handleVideoReady, { once: true });

    video.addEventListener('canplaythrough', handleVideoReady, { once: true });

    video.addEventListener('ended', () => {
      if (video.loop) {
        video.currentTime = 0;
        video.play().catch(() => {});
      }
    });

    video.addEventListener('error', (e) => {
      console.error('HeroResponsiveVideo: Video load error', {
        url: videoUrl,
        error: e,
        video: video,
        errorCode: video.error?.code,
        errorMessage: video.error?.message
      });
      video.remove();
    });

    console.log('HeroResponsiveVideo: Loading video', {
      url: videoUrl,
      isDesktop,
      deviceType: video.dataset.deviceType,
      element: this,
      container: this.refs.videoContainer
    });

    video.load();

    if (video.readyState >= 3) {
      handleVideoReady();
    } else {
      setTimeout(() => {
        if (video.readyState >= 3 && !videoReady) {
          handleVideoReady();
        }
      }, 100);
    }
  }
}

if (!customElements.get('hero-responsive-video')) {
  customElements.define('hero-responsive-video', HeroResponsiveVideo);
}
